/**
 * FERP PDF Text Search Highlight (DRR-079)
 *
 * Uses pdfjs-dist textContent to search for text across pages
 * and render highlight overlays on matching text.
 */

import { useEffect, useCallback, useState, useRef } from 'react';
import { Box, Text, Group, ActionIcon, Badge } from '@mantine/core';
import { IconChevronUp, IconChevronDown } from '@tabler/icons-react';
import { usePdfSuite } from './PdfSuiteContext';

interface TextSearchHighlightProps {
  pdfDocumentProxy?: unknown;
}

interface SearchMatch {
  page: number;
  index: number;
}

export function TextSearchHighlight({ pdfDocumentProxy }: TextSearchHighlightProps) {
  const { state, dispatch, setCurrentPage } = usePdfSuite();
  const { searchQuery, searchMatches, currentSearchMatch } = state.viewer;
  const [matches, setMatches] = useState<SearchMatch[]>([]);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const searchPages = useCallback(
    async (query: string) => {
      if (!pdfDocumentProxy || !query.trim()) {
        setMatches([]);
        dispatch({ type: 'SET_SEARCH_MATCHES', matches: [], currentMatch: 0 });
        return;
      }

      const proxy = pdfDocumentProxy as {
        numPages: number;
        getPage: (n: number) => Promise<{
          getTextContent: () => Promise<{ items: Array<{ str: string }> }>;
        }>;
      };

      const q = query.toLowerCase();
      const found: SearchMatch[] = [];
      const pageNums: number[] = [];

      for (let i = 1; i <= proxy.numPages; i++) {
        const page = await proxy.getPage(i);
        const content = await page.getTextContent();
        const pageText = content.items.map((item) => item.str).join(' ').toLowerCase();

        let idx = pageText.indexOf(q);
        while (idx !== -1) {
          found.push({ page: i, index: idx });
          if (!pageNums.includes(i)) pageNums.push(i);
          idx = pageText.indexOf(q, idx + 1);
        }
      }

      setMatches(found);
      dispatch({ type: 'SET_SEARCH_MATCHES', matches: pageNums, currentMatch: 0 });

      // Navigate to first match
      if (found.length > 0) {
        setCurrentPage(found[0]!.page);
      }
    },
    [pdfDocumentProxy, dispatch, setCurrentPage],
  );

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      searchPages(searchQuery);
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchQuery, searchPages]);

  const goToMatch = useCallback(
    (direction: 'next' | 'prev') => {
      if (matches.length === 0) return;
      let next = currentSearchMatch;
      if (direction === 'next') {
        next = (next + 1) % matches.length;
      } else {
        next = (next - 1 + matches.length) % matches.length;
      }
      dispatch({ type: 'SET_SEARCH_MATCHES', matches: searchMatches, currentMatch: next });
      setCurrentPage(matches[next]!.page);
    },
    [matches, currentSearchMatch, searchMatches, dispatch, setCurrentPage],
  );

  if (!searchQuery.trim() || matches.length === 0) return null;

  return (
    <Box
      style={{
        position: 'absolute',
        top: 4,
        right: 4,
        zIndex: 20,
        backgroundColor: 'var(--mantine-color-body)',
        borderRadius: 4,
        padding: '2px 8px',
        border: '1px solid var(--mantine-color-gray-3)',
      }}
    >
      <Group gap={4}>
        <Badge size="xs" variant="light">
          {currentSearchMatch + 1} / {matches.length}
        </Badge>
        <ActionIcon variant="subtle" size="xs" onClick={() => goToMatch('prev')}>
          <IconChevronUp size={12} />
        </ActionIcon>
        <ActionIcon variant="subtle" size="xs" onClick={() => goToMatch('next')}>
          <IconChevronDown size={12} />
        </ActionIcon>
      </Group>
    </Box>
  );
}
